import React from "react";

import * as R from "ramda";
import { withProps, componentFromProp, compose } from "recompose";

import { Box, Flex, Link, Text } from "src/components/base";
import RelatedLinks from "src/components/RelatedLinks";
import ComponentForType from "src/utils/ComponentForType";

import styled from "react-emotion";

import ItemCarousel from "./ItemCarousel";
import PhotoWithCaption from "./PhotoWithCaption";
import contentTypes from "./contentTypes";

import { shevy, themeColor } from "src/styles";

const Title = withProps(() => ({
  fontWeight: "bold",
  textTransform: "uppercase",
  style: shevy.h2,
}))(Text);

const ColorfulBoxes = ({ items }) =>
  items ? (
    <div>
      <Flex
        flexDirection={["column", "column", "column", "row"]}
        py={[0, 0, 0, 3]}
        px={[0, 0, 0, 2, 4]}
        mx={[0, 0, 0, -2]}
      >
        {items.map(item => (
          <Flex
            key={item.id}
            flex={[undefined, undefined, undefined, 1]}
            mx={[0, 0, 0, 2]}
          >
            <ComponentForType item={item} types={contentTypes} />
          </Flex>
        ))}
      </Flex>
    </div>
  ) : null;

const enhancePhotoWithCaptions = compose(
  withProps(({ items, itemsToShow }) => {
    const length = itemsToShow || items.length;

    return {
      items: R.slice(0, length, items),
      hasMore: items.length > length,
    };
  })
);

const SeeAll = styled(Link)({
  height: 64,
  width: 64,
  borderRadius: 32,
  position: "absolute",
  right: -32,
  bottom: 150,
});

const PhotosWithCaptions = enhancePhotoWithCaptions(
  ({ hasMore, items, title, theme }) => (
    <Box py={[3, 3, 4]} px={[3, 3, 4]} style={{ position: "relative" }}>
      {title && <Title color={themeColor(theme)}>{title}</Title>}
      <Flex
        flexDirection={["column", "column", "row"]}
        alignItems="stretch"
        mx={-2}
        mt={title ? [3, 3, 4] : 0}
      >
        {items.map((item, index) => (
          <Flex
            key={item.id}
            flex={[undefined, undefined, 1]}
            mx={2}
            mb={[4, 4, 0]}
          >
            <PhotoWithCaption item={item} height={300} />
          </Flex>
        ))}
      </Flex>
      {hasMore && (
        <SeeAll
          to="#"
          bg="blue"
          align="center"
          justify="center"
          boxShadow={2}
          p={1}
          display={["none", "none", "flex"]}
        >
          <Text
            color="white"
            fontSize={15}
            fontWeight="bold"
            style={{ textAlign: "center", lineHeight: 1 }}
          >
            SEE ALL
          </Text>
        </SeeAll>
      )}
    </Box>
  )
);

const enhanceLinkList = withProps(({ items }) => ({
  links: R.map(
    item => ({
      name: item.title,
      url: item.url,
    }),
    items
  ),
}));

const LinkList = enhanceLinkList(
  ({
    hasMore,
    links,
    title,
    theme,
    parentTheme,
    containerProps,
    padded = true,
    itemProps,
    titleProps,
  }) => (
    <Box py={padded && [3, 3, 4]} px={padded && [3, 3, 4]} {...containerProps}>
      <RelatedLinks
        title={title}
        links={links}
        theme={theme || parentTheme}
        titleProps={titleProps}
        itemProps={itemProps}
      />
    </Box>
  )
);

const Grid = ({
  items,
  parentTheme,
  itemProps,
  titleProps,
  containerProps,
}) => (
  <Flex wrap="wrap" style={{ alignItems: "flex-start" }}>
    {items.map(item => (
      <Box
        flex={[
          "0 0 100%",
          "0 0 50%",
          "0 0 50%",
          "0 0 50%",
          items.length > 2 ? "0 0 33.33%" : undefined,
        ]}
        key={item.id}
      >
        <ItemGroupEmbed
          {...item}
          theme={parentTheme}
          containerProps={containerProps}
          itemProps={itemProps}
          titleProps={titleProps}
        />
      </Box>
    ))}
  </Flex>
);

const styleComponents = {
  colorfulboxes: ColorfulBoxes,
  photoswithcaptions: PhotosWithCaptions,
  linklist: LinkList,
  grid: Grid,
  carousel: ItemCarousel,
};

const ItemGroupEmbed = withProps(props =>
  R.pipe(
    R.prop("embedStyle"),
    R.when(R.isNil, R.always("Colorful Boxes")),
    R.replace(/\s/g, ""),
    R.toLower,
    R.propOr(ColorfulBoxes, R.__, styleComponents),
    R.objOf("component")
  )(props)
)(componentFromProp("component"));

export default ItemGroupEmbed;

export const itemGroupFragment = graphql`
  fragment ItemGroupContent on ContentfulItemGroup {
    id
    __typename
    title
    embedStyle
    theme
    itemsToShow
    items {
      __typename
      ...IconButtonPage
      ...Callout
      ...ArticleWithPhoto
      ...PageWithPhoto
      ...PhotoWithCaptionContents
      ... on ContentfulPage {
        title
        url
      }
      ... on ContentfulCalculator {
        title: name
        url
      }
      ... on ContentfulArticle {
        title
        url
      }
      ... on ContentfulLink {
        title: name
        url
      }
    }
  }

  fragment ItemGroupContents on ContentfulItemGroup {
    ...ItemGroupContent
    items {
      ... on ContentfulItemGroup {
        ...ItemGroupContent
      }
    }
  }
`;



// WEBPACK FOOTER //
// ./src/components/ItemGroup/ItemGroup.js